import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Clock, BarChart3, TrendingUp, Plus, Minus } from "lucide-react";
import "./HourlyBreakdown.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "Hour by Hour Work Activity and Productivity Tracking | TeamGrid";
const DESCRIPTION =
  "Analyze work hour by hour with detailed activity logs, app usage insights and productivity scores for complete daily transparency.";
const CANONICAL = "https://teamgrid.ai/features/hourly-breakdown";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const TEAL = "#00D1C1";
const ICON_BG = "#E0FFFC";
const CARD_BORDER = "#EDF4FF";
const GREEN = "#34A851";
const PURPLE = "#6C59A6";
const YELLOW = "#FACC15";

const timelineRows = [
  {
    time: "10:00 - 11:00",
    duration: "39m",
    percent: "85%",
    bar: [
      { w: 67, c: GREEN },
      { w: 33, c: PURPLE },
    ],
  },
  {
    time: "11:00 - 12:00",
    duration: "60m",
    percent: "85%",
    bar: [
      { w: 72, c: GREEN },
      { w: 28, c: YELLOW },
    ],
  },
  {
    time: "12:00 - 13:00",
    duration: "47m",
    percent: "85%",
    bar: [
      { w: 62, c: GREEN },
      { w: 38, c: PURPLE },
    ],
  },
];

const HourlyBreakdown = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  // FAQ data specific to Hourly Breakdown — single Q&A
  const hourlyBreakdownFAQs = [
    {
      question: "How does Hourly Breakdown help understand productivity?",
      answer:
        "Hourly Breakdown visualizes how work time is distributed throughout the day so teams can spot peak performance hours, identify distractions, and optimize schedules.",
    },
  ];

  const features = [
    {
      icon: Clock,
      title: "Hour-by-Hour View",
      sub: "See exactly what happened during each hour of work",
    },
    {
      icon: BarChart3,
      title: "Activity Breakdown",
      sub: "Apps, websites, and tasks for each time block",
    },
    {
      icon: TrendingUp,
      title: "Productivity Scores",
      sub: "Individual scores for each hour of the day",
    },
  ];

  return (
    <div
      className="hourly-breakdown-container"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How does TeamGrid help recover lost billable hours?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "TeamGrid surfaces unlogged work and improves visibility into billable time, helping service teams recover lost revenue.",
                },
              },
              {
                "@type": "Question",
                name: "Does TeamGrid show utilization by individual and team?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, TeamGrid provides utilization insights at both individual and team levels to identify efficiency gaps.",
                },
              },
              {
                "@type": "Question",
                name: "Is manual timesheet entry required?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No, TeamGrid automatically captures productivity data, eliminating manual timesheets while improving billable time accuracy.",
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div className="max-w-7xl mx-auto">
                <div className="mb-0 w-full text-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-6 flex justify-center"
                  >
                    <div
                      className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-wide"
                      style={{
                        color: TEAL,
                        borderColor: TEAL,
                        background: ICON_BG,
                      }}
                    >
                      <Zap className="h-4 w-4" style={{ color: TEAL }} />
                      <span>TIME ANALYTICS</span>
                    </div>
                  </motion.div>
                  <h1
                    className="mb-4 font-bold text-black"
                    style={{
                      fontSize: "clamp(2.5rem, 5vw, 62px)",
                      lineHeight: "clamp(50px, 8vw, 72px)",
                    }}
                  >
                    Hourly <span style={{ color: TEAL }}>Breakdown</span>
                  </h1>
                  <p
                    className="mx-auto max-w-2xl text-center"
                    style={{ color: "rgba(0,0,0,0.6)", fontSize: 16 }}
                  >
                    Detailed hour-by-hour activity analysis. See exactly how
                    each hour of the workday was spent.
                  </p>
                </div>
              </div>
            </div>

            {/* Main content - white bg; spec: padding 80px, container gap 64px, max-w 1280px */}
            <section
              className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 bg-white"
              style={{ paddingTop: 80, paddingBottom: 40 }}
            >
              <div
                className="mb-6 flex w-full max-w-[1280px] flex-col lg:flex-row lg:items-center lg:justify-center"
                style={{ gap: 64 }}
              >
                {/* Left column - Granular Time Intelligence; spec: 608px, gap 20px, H2 48px/53px */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-1 flex-col items-start lg:max-w-[608px]"
                  style={{ gap: 20 }}
                >
                  <h2
                    className="font-bold text-black"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 700,
                      fontSize: 48,
                      lineHeight: "53px",
                    }}
                  >
                    Granular Time Intelligence
                  </h2>
                  <p
                    className="text-left"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      fontSize: 17,
                      lineHeight: "24px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Break down each day into hourly segments with detailed
                    activity logs, productivity scores, and work summaries for
                    complete transparency.
                  </p>

                  <div
                    className="flex flex-col"
                    style={{ padding: "24px 0", gap: 32 }}
                  >
                    {features.map((f, i) => (
                      <div
                        key={i}
                        className="flex items-start"
                        style={{ gap: 24 }}
                      >
                        <div
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                          style={{
                            background: ICON_BG,
                            boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                          }}
                        >
                          <f.icon className="h-5 w-5" style={{ color: TEAL }} />
                        </div>
                        <div className="flex flex-col" style={{ gap: 7 }}>
                          <h3
                            className="font-bold text-black"
                            style={{
                              fontFamily: "Inter",
                              fontSize: 15,
                              lineHeight: "28px",
                            }}
                          >
                            {f.title}
                          </h3>
                          <p
                            style={{
                              fontFamily: "Inter",
                              fontSize: 15,
                              lineHeight: "23px",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {f.sub}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate("/book-demo")}
                    className="font-bold text-black"
                    style={{
                      padding: "12px 32px",
                      background: TEAL,
                      boxShadow: "0px 0px 15px rgba(0, 209, 193, 0.3)",
                      borderRadius: 12,
                      fontSize: 16,
                      lineHeight: "24px",
                    }}
                  >
                    Explore Report
                  </motion.button>
                </motion.div>

                {/* Right column - HOURLY TIMELINE card; spec: gradient #F9FAFB→#FFF, border #EDF4FF, radius 32px */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-1 flex-col lg:max-w-[608px] overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(132.88deg, #F9FAFB 4.02%, #FFFFFF 94%)",
                    border: `1px solid ${CARD_BORDER}`,
                    boxShadow: "0px 20px 40px -3px rgba(0, 0, 0, 0.1)",
                    borderRadius: 32,
                  }}
                >
                  <div
                    className="flex flex-col"
                    style={{ padding: 24, gap: 4 }}
                  >
                    <div className="flex items-center" style={{ gap: 8 }}>
                      <Clock
                        className="h-5 w-5 flex-shrink-0"
                        style={{ color: TEAL }}
                      />
                      <h3
                        className="font-bold text-black"
                        style={{
                          fontFamily: "Inter",
                          fontSize: 20,
                          lineHeight: "28px",
                        }}
                      >
                        Hourly Timeline
                      </h3>
                    </div>
                    <p
                      style={{
                        fontFamily: "Inter",
                        fontSize: 12,
                        lineHeight: "16px",
                        fontWeight: 500,
                        color: "rgba(0,0,0,0.6)",
                      }}
                    >
                      Monday, Nov 24, 2025
                    </p>
                  </div>

                  <div
                    className="flex flex-col"
                    style={{ padding: "0 24px 24px", gap: 16 }}
                  >
                    {timelineRows.map((row, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col border bg-white"
                        style={{
                          padding: 16,
                          gap: 12,
                          borderColor: CARD_BORDER,
                          borderRadius: 16,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className="font-bold"
                            style={{
                              fontSize: 12,
                              lineHeight: "16px",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {row.time}
                          </span>
                          <div className="flex items-center gap-3">
                            <span
                              className="font-bold"
                              style={{
                                fontSize: 12,
                                lineHeight: "16px",
                                color: "#A0A0A0",
                              }}
                            >
                              {row.duration}
                            </span>
                            <span
                              className="font-bold"
                              style={{
                                fontSize: 12,
                                lineHeight: "16px",
                                color: GREEN,
                              }}
                            >
                              {row.percent}
                            </span>
                          </div>
                        </div>
                        <div
                          className="flex h-3 w-full overflow-hidden rounded-full"
                          style={{ background: "#EDF4FF" }}
                        >
                          {row.bar.map((seg, i) => (
                            <div
                              key={i}
                              className={`h-full ${i === 0 ? "rounded-l-full" : ""} ${i === row.bar.length - 1 ? "rounded-r-full" : ""}`}
                              style={{ width: `${seg.w}%`, background: seg.c }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - Light Grey Background (full-bleed left/right) */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 px-4 sm:px-6 md:px-8 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid for hourly
                    productivity analysis.
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {hourlyBreakdownFAQs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HourlyBreakdown;
